'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchServices } from '@eliadmin/core/src/api/services/services-api';
import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import type { Service } from '../../types/service';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { FaMinus } from 'react-icons/fa';

const CategoryItem = ({ service, layer }: { service: Service; layer?: number }) => {
  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={service.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full flex-1 flex-row items-center justify-between overflow-hidden whitespace-normal border-none',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <Link prefetchOnHover
          href={
            service.name === 'Tvorba eshopu na mieru' ? 'https://eliadmin.sk' : `/${service.slug}`
          }
          className="ml-4 flex-1"
          prefetch={false}
        >
          <SheetClose
            className={cn(
              'flex items-center justify-start gap-4 text-left text-base font-light text-foreground',
            )}
          >
            <FaMinus className="text-primary" />
            {service.name}
          </SheetClose>
        </Link>
      </li>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const locale = 'sk';
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const { t } = useTranslations();

  const { data: services = [] } = useQuery({
    queryKey: [CacheKey.REFERENCES, locale],
    queryFn: () => fetchServices({ locale }),
  });

  return (
    <>
      <Sheet>
        <SheetTrigger aria-label="Menu"
          className={cn('text-primary', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          {HEADER_CONFIG?.sidebar?.triggerIcon}
        </SheetTrigger>
        <SheetContent
          side={'right'}
          className={cn(
            'top-[76px] flex h-full w-screen flex-col items-start justify-start gap-12 overflow-scroll border-none bg-white p-6 pb-24',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'hidden',
          }}
        >
          <ul
            className={cn(
              'ea-menu-sidebar__list items-starts flex flex-col justify-start gap-3',
              // className?.list,
            )}
          >
            {!!props.menu && (
              <div className={cn('flex flex-col gap-3', 'ea-mobile-sidebar__menu-container')}>
                {props.menu?.map((item, index) => {
                  const Comp = item.path
                    ? isRelativeHref(getSafeHref(item.path))
                      ? Link
                      : 'a'
                    : 'span';
                  return (
                    <>
                      {index === 1 ? (
                        <Accordion
                          type="single"
                          collapsible
                          className={cn(
                            'flex w-full flex-col items-start justify-start gap-3',
                            'ea-mobile-sidebar__service-accordion',
                          )}
                        >
                          <AccordionItem value="sluzby">
                            <AccordionTrigger
                              className={cn(
                                'relative flex w-full items-center justify-between py-0 text-lg font-bold uppercase text-primary',
                                'ea-mobile-sidebar__accordion-first-trigger',
                              )}
                            >
                              {t('text1000')}
                            </AccordionTrigger>
                            <AccordionContent className="mt-6 flex flex-col gap-6">
                              {services?.map((service, index) => (
                                <CategoryItem key={index} service={service} />
                              ))}
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      ) : (
                        <SheetClose key={index} asChild>
                          <Comp
                            href={item.path ? getSafeHref(item.path) : (undefined as any)}
                            className="flex w-full"
                            prefetch={false}
                          >
                            <div className={cn('text-lg font-bold', 'uppercase text-foreground')}>
                              {item.name}
                            </div>
                          </Comp>
                        </SheetClose>
                      )}
                    </>
                  );
                })}
              </div>
            )}
          </ul>
          <SheetClose asChild className="w-fit">
            <Button asChild className="w-fit">
              <Link prefetchOnHover
                prefetch={false}
                href={'https://eliadmin.elias-itsolutions.sk/account/login'}
                target="_blank"
              >
                Vyskúšajte eliadmin
              </Link>
            </Button>
          </SheetClose>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
